import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { dialogsEndpoints as endpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { Dialog, Dialogs } from '@/shared/types/dialog';

import { selectBrainId } from '../redux/selectors';

export const useDialogsList = (): Dialog[] | null => {
  const brainId = useSelector(selectBrainId);

  const { data } = useQuery<Dialogs, Error>({
    queryKey: [endpoints.dialogs(brainId)],
    queryFn: () => callGet(endpoints.dialogs(brainId)),
    enabled: !!brainId,
  });

  return data?.dialogs ?? null;
};

const EMPTY_INDEX: ReadonlyMap<string, Dialog> = new Map();

const indexCache = new WeakMap<Dialog[], ReadonlyMap<string, Dialog>>();

/** One pass over every node of every dialog, cached on the array react-query hands back. */
export const dialogsByNode = (dialogs: Dialog[] | null): ReadonlyMap<string, Dialog> => {
  if (!dialogs) {
    return EMPTY_INDEX;
  }

  const cached = indexCache.get(dialogs);
  if (cached) {
    return cached;
  }

  const index = new Map<string, Dialog>();
  dialogs.forEach((dialog) => {
    if (Array.isArray(dialog.nodes)) {
      dialog.nodes.forEach((node) => index.set(node.node_id, dialog));
    }
  });
  indexCache.set(dialogs, index);

  return index;
};

export const findDialogOfNode = (dialogs: Dialog[] | null, nodeId?: string): Dialog | null =>
  nodeId ? (dialogsByNode(dialogs).get(nodeId) ?? null) : null;

const EMPTY_NAMES: ReadonlyMap<string, string> = new Map();

const nameCache = new WeakMap<Dialog[], ReadonlyMap<string, string>>();

const nodeNamesById = (dialogs: Dialog[] | null): ReadonlyMap<string, string> => {
  if (!dialogs) {
    return EMPTY_NAMES;
  }

  const cached = nameCache.get(dialogs);
  if (cached) {
    return cached;
  }

  const names = new Map<string, string>();
  dialogs.forEach((dialog) => {
    if (Array.isArray(dialog.nodes)) {
      dialog.nodes.forEach((node) => {
        if (node.name) {
          names.set(node.node_id, node.name);
        }
      });
    }
  });
  nameCache.set(dialogs, names);

  return names;
};

export const useNodeName = (nodeId?: string): string | null => {
  const dialogs = useDialogsList();

  if (!nodeId) {
    return null;
  }
  return nodeNamesById(dialogs).get(nodeId) ?? null;
};
